import React, {useState, useEffect, useRef} from "react";
import {Table, Form, Input, Switch, Button} from "antd";
import {useFormik, FormikProvider, getIn} from "formik";
import {useTranslation} from "react-i18next";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CompanyContanier, CompanyButtonContainer} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const ShiftSuggestionOptions = ({companyId}) => {
    const stores = useStore();
    const {t} = useTranslation(["shiftSuggestion"]);

    const [isSuggestFormChanged, setIsSuggestFormChanged] = useState(false);
    const [suggestFormData, setSuggestFormData] = useState();
    const [companyRecall, setCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                locationCheck: {
                    isActive: companyData?.data?.options?.shiftSuggestion?.isActive,
                    limit: companyData?.data?.options?.shiftSuggestion?.limit
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);
            await stores.companyStore.updateShiftSuggestionOptions(values);
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsSuggestFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isSuggestFormChanged) {
            setSuggestFormData(values);
        } else if (values && JSON.stringify(suggestFormData) !== JSON.stringify(values)) {
            setIsSuggestFormChanged(true);
        } else {
            setIsSuggestFormChanged(false);
        }
    }, [values]);

    return (
        <div data-testid="shiftSuggestionOptionsScreen">
            <h2>{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="shiftSuggestionOptionsForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsSuggestFormChanged(true)}
                >
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        data-testid="shiftSuggestionOptionsTable"
                        pagination={false}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <ErrorFocus formik={formik} />
                        <FormikProvider value={formik}>
                            <Column
                                title={t("shiftSuggestion", {ns: "shiftSuggestion"})}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values?.options?.shiftSuggestion?.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="shiftSuggestionOptionSwitch"
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.shiftSuggestion.isActive",
                                                    e
                                                );
                                                setIsSuggestFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={t("warehouseCourierDistance", {
                                    ns: "shiftSuggestion"
                                })}
                                dataIndex="limit"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.locationCheck.limit`) &&
                                            getIn(touched, `options.locationCheck.limit`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.locationCheck.limit`) &&
                                            getIn(touched, `options.locationCheck.limit`)
                                                ? getIn(errors, `options.locationCheck.limit`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="limit"
                                            data-testid="limitInput"
                                            value={
                                                values?.options?.shiftSuggestion?.distance ||
                                                companyData?.data?.options?.shiftSuggestion
                                                    ?.distance
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.shiftSuggestion.distance",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values?.options?.shiftSuggestion?.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                        </FormikProvider>
                    </Table>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Button
                            htmlType="submit"
                            disabled={!isSuggestFormChanged || isSubmitting}
                            type="primary"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
        </div>
    );
};
